<template>
  <div class="flex flex-col gap-6">
    <div class="bg-primary30 rounded-lg p-4 md:px-6 text-white text-left">
      <h3 class="font-bold text-lg mb-2">O que são políticas, no Minha Escala?</h3>
      <p class="text-sm leading-relaxed mb-3">
        As políticas são configurações modulares que podem ser conectadas a uma posição, escala,
        unidade ou empresa. Cada política funciona como um conjunto de regras que padroniza o
        processo operacional — desde quais profissionais estão autorizados a assumir plantões até os
        critérios de pagamento e a forma de registrar a presença.
      </p>
      <p class="text-sm leading-relaxed mb-3">
        Por serem modulares, as políticas podem ser conectadas e desconectadas a qualquer momento.
        Ao desconectá-la, seu conjunto de regras é preservado como uma política inativa, disponível
        para ser reutilizada posteriormente.
      </p>
      <h4 class="font-bold text-base mb-1">Vínculos de políticas</h4>
      <p class="text-sm leading-relaxed">
        Vínculo é o ato de conectar uma política a uma escala ou unidade. É nele que se define qual
        política está em vigência, a partir de dois critérios: as datas de vigência configuradas e a
        escolha de mantê-la ativa ou inativa. Para que as configurações de uma política sejam
        herdadas, as duas condições precisam ser atendidas simultaneamente. Uma política ativa,
        porém fora do período de vigência, não tem suas regras aplicadas — assim como uma política
        dentro da vigência, mas inativa, também não é herdada.
      </p>
    </div>

    <div class="flex flex-col gap-3">
      <div v-if="flags.enabledPolicyModules" class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <SelectCard
          :icon="mdiTextBoxEditOutline"
          title="Qualificação Documental"
          description="Análise de documentação para alocação e faturamento"
          layout="vertical"
          data-testid="qualification-doc-card"
          @click="router.push(PATHS.QUALIFICATION_DOC)"
        />
        <SelectCard
          :icon="mdiEmailOpenMultipleOutline"
          title="Qualificação para Repasse"
          description="Análise de conformidade para pagamento"
          layout="vertical"
          data-testid="qualification-transfer-card"
          @click="router.push(PATHS.QUALIFICATION_TRANSFER)"
        />
      </div>
      <SelectCard
        :icon="mdiFileDocumentOutline"
        title="Política de Restrição"
        description="Análise de documentação para alocação e faturamento"
        data-testid="restriction-policy-card"
        @click="router.push(PATHS.POLICIES)"
      />
      <div v-if="flags.enabledPolicyModules" class="grid grid-cols-1 gap-3">
        <SelectCard
          :icon="mdiTrayArrowUp"
          title="Importar validações"
          description="Criar política a partir de arquivo externo"
          data-testid="import-validations-card"
          @click="openSelectTypeModal"
        />
      </div>
    </div>

    <SelectQualificationTypeModal v-model="showSelectTypeModal" @select-type="handleSelectType" />

    <ImportQualificationValidationsModal
      v-model="showImportModal"
      :policy-type="importPolicyType"
      @imported="onImported"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import {
  mdiTextBoxEditOutline,
  mdiEmailOpenMultipleOutline,
  mdiFileDocumentOutline,
  mdiTrayArrowUp
} from '@mdi/js'

import PATHS from '@/router/paths'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import SelectCard from '@/components/designSystem/Cards/SelectCard.vue'
import SelectQualificationTypeModal from '@/modules/company/DsPolicy/components/SelectQualificationTypeModal.vue'
import ImportQualificationValidationsModal from '@/modules/company/DsPolicy/components/ImportQualificationValidationsModal/Index.vue'
import type { QualificationPolicyType } from '@/modules/company/DsPolicy/components/ImportQualificationValidationsModal/types'

const router = useRouter()
const headerStore = useHeaderStore()
const { flags } = useFeatureFlags()
const showSelectTypeModal = ref(false)
const showImportModal = ref(false)
const importPolicyType = ref<QualificationPolicyType>('TRANSFER_QUALIFICATION')

const openSelectTypeModal = () => {
  showSelectTypeModal.value = true
}

const handleSelectType = (policyType: QualificationPolicyType) => {
  importPolicyType.value = policyType
  showImportModal.value = true
}

const onImported = () => {
  router.push(
    importPolicyType.value === 'DOCUMENTARY_QUALIFICATION'
      ? PATHS.QUALIFICATION_DOC
      : PATHS.QUALIFICATION_TRANSFER
  )
}

onMounted(() => {
  headerStore.setTitle('Políticas', 'Configure políticas para as escalas e unidades')
})
</script>
